/**
 * Session vault — surface-scoped auth session isolation.
 *
 * PROBLEM. There is ONE Supabase browser client (`src/integrations/supabase/client.ts`
 * is auto-generated and must not be edited), so there is ONE `sb-*-auth-token`
 * key in localStorage. Walking the consumer funnel at `/create` and completing
 * OTP verification writes a consumer session over that key — evicting the
 * staff (super_admin) session that was there.
 *
 * WHY NOT TWO CLIENTS. A second client with its own `storageKey` would isolate
 * the *auth* calls, but every consumer screen also reads/writes data through
 * the shared `src/services/*` layer, which imports the single generated client.
 * Two clients means those service calls would run under the WRONG identity
 * (staff token for consumer rows and vice-versa) unless all ~35 service modules
 * are re-plumbed to take a client argument. So instead of two clients we keep
 * one client and two *stored* sessions, one per surface:
 *
 *   scope "consumer"  → routes owned by the funnel (/create, /account, ...)
 *   scope "staff"     → everything else (/admin/*, /platform/*, ...)
 *
 * Both sessions coexist in localStorage. The client holds whichever one belongs
 * to the surface you are on: signing in as a consumer at /create no longer
 * destroys the staff session — navigating back to /platform restores it.
 * (One ACTIVE session per tab is inherent to a single client; two *simultaneously*
 * active identities in one tab would still need a second browser profile.)
 *
 * Legitimate case preserved: a real customer who IS the signed-in user just has
 * a "consumer" scope entry and no "staff" entry — nothing is stashed, nothing is
 * restored, and they stay signed in across the funnel exactly as before.
 */
import { supabase } from "@/integrations/supabase/client";
import type { Session } from "@supabase/supabase-js";

export type SessionScope = "consumer" | "staff";

interface VaultEntry {
  access_token: string;
  refresh_token: string;
  user_id: string;
  saved_at: number;
}

const KEY_PREFIX = "cardmoji_session_vault_";

/** Consumer-owned surfaces. Everything else counts as staff. */
const CONSUMER_PATHS = [
  "/create",
  "/account",
  "/standing",
  "/creator",
  "/r/",
  "/t/",
  "/c/",
];

export function scopeForPath(pathname: string): SessionScope {
  return CONSUMER_PATHS.some((p) => pathname === p || pathname.startsWith(p))
    ? "consumer"
    : "staff";
}

export function readVault(scope: SessionScope): VaultEntry | null {
  try {
    const raw = localStorage.getItem(KEY_PREFIX + scope);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as VaultEntry;
    if (!parsed?.access_token || !parsed?.refresh_token) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function writeVault(scope: SessionScope, session: Session) {
  if (!session.refresh_token || !session.access_token || !session.user?.id) return;
  const entry: VaultEntry = {
    access_token: session.access_token,
    refresh_token: session.refresh_token,
    user_id: session.user.id,
    saved_at: Date.now(),
  };
  try {
    localStorage.setItem(KEY_PREFIX + scope, JSON.stringify(entry));
  } catch {
    /* storage full / disabled — isolation degrades to previous behaviour */
  }
}

export function clearVault(scope?: SessionScope) {
  const scopes: SessionScope[] = scope ? [scope] : ["consumer", "staff"];
  for (const s of scopes) {
    try {
      localStorage.removeItem(KEY_PREFIX + s);
    } catch {
      /* ignore */
    }
  }
}

/** Hand a stashed scope session back to the client. */
export async function restoreScope(scope: SessionScope): Promise<Session | null> {
  const entry = readVault(scope);
  if (!entry) return null;
  const { data, error } = await supabase.auth.setSession({
    access_token: entry.access_token,
    refresh_token: entry.refresh_token,
  });
  if (error || !data.session) {
    // The stashed refresh token is spent or revoked — drop it so we don't
    // retry a dead session on every route change.
    clearVault(scope);
    return null;
  }
  writeVault(scope, data.session);
  return data.session;
}

/**
 * Recovery for a transient null session: the supabase-js client drops the
 * session locally when one refresh call fails (iframe suspension, network
 * blip) even though the server session is still valid. Try, in order:
 * the client's own storage, an explicit refresh, then the scope vault.
 */
export async function recoverSession(scope: SessionScope): Promise<Session | null> {
  const current = await supabase.auth.getSession();
  if (current.data.session) return current.data.session;

  try {
    const refreshed = await supabase.auth.refreshSession();
    if (refreshed.data.session) {
      writeVault(scope, refreshed.data.session);
      return refreshed.data.session;
    }
  } catch {
    /* fall through to the vault */
  }

  return restoreScope(scope);
}
